import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@listto/shared/solid";
import {
  calculateCompletionPercentage,
  cn,
  getListsCountQuery,
  getTodoStatsQuery,
  mapTodoStats,
  queryKeys,
} from "@listto/shared";
import { BarChart3 } from "lucide-solid";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@/utils/number";

interface StatsSectionProps {
  userId: string;
}

export function StatsSection(props: StatsSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const stats = createPowerSyncQuery({
    queryKey: () => queryKeys.todos.stats(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getTodoStatsQuery(dbInstance, props.userId) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const lists = createPowerSyncQuery({
    queryKey: () => queryKeys.lists.count(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getListsCountQuery(dbInstance, props.userId) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const listsCount = () => lists.data?.[0]?.count ?? 0;

  const todoStats = () => mapTodoStats(stats.data?.[0]);
  const completionPct = () =>
    calculateCompletionPercentage(todoStats().completed, todoStats().total);

  return (
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2">
          <BarChart3 class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Statistics`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={t(msg`Total`)}
            value={todoStats().total}
            colorClass="text-primary"
            bgClass="bg-primary/10"
            isLoading={stats.isPending}
          />
          <StatCard
            label={t(msg`Completed`)}
            value={todoStats().completed}
            colorClass="text-success"
            bgClass="bg-success/10"
            isLoading={stats.isPending}
          />
          <StatCard
            label={t(msg`Important`)}
            value={todoStats().important}
            colorClass="text-warning"
            bgClass="bg-warning/10"
            isLoading={stats.isPending}
          />
          <StatCard
            label={t(msg`Lists`)}
            value={listsCount()}
            colorClass="text-foreground"
            bgClass="bg-muted/50"
            isLoading={lists.isPending}
          />
        </div>

        <Show when={todoStats().total > 0 && !stats.isPending}>
          <div class="space-y-2">
            <div class="flex items-center justify-between text-xs text-muted-foreground">
              <span>{t(msg`Total progress`)}</span>
              <span class="font-bold text-foreground">{completionPct()}%</span>
            </div>
            <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                class="h-full bg-green-500 transition-transform duration-500 origin-left"
                style={{ transform: `scaleX(${completionPct() / 100})` }}
              />
            </div>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}

function StatCard(props: {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
  isLoading?: boolean;
}) {
  const { locale } = useI18n();
  const isCompact = () => props.value >= COMPACT_THRESHOLD;
  const formattedValue = () => formatCompactNumber(props.value, locale());
  const fullLabel = () => `${props.value.toLocaleString(locale())} ${props.label.toLowerCase()}`;

  return (
    <div
      class={cn(
        "flex flex-col items-center justify-center rounded-2xl p-4 text-center transition-all hover:scale-[1.02]",
        props.bgClass,
      )}
    >
      <Show
        when={props.isLoading}
        fallback={
          <Show
            when={isCompact()}
            fallback={
              <span
                class={cn(
                  "text-2xl font-black leading-none tracking-tight select-none",
                  props.colorClass,
                )}
                aria-label={fullLabel()}
              >
                {props.value}
              </span>
            }
          >
            <Tooltip openDelay={350} closeDelay={100}>
              <TooltipTrigger class="focus-visible:outline-none select-none cursor-help">
                <span
                  class={cn(
                    "text-2xl font-black leading-none tracking-tight border-b border-dashed border-current pb-0.5",
                    props.colorClass,
                  )}
                  aria-label={fullLabel()}
                >
                  {formattedValue()}
                </span>
              </TooltipTrigger>
              <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
                {fullLabel()}
              </TooltipContent>
            </Tooltip>
          </Show>
        }
      >
        <Skeleton class="h-7 w-8" />
      </Show>
      <div class="mt-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-80 select-none">
        {props.label}
      </div>
    </div>
  );
}
